import Vue, { PluginObject } from 'vue'
import { $_bridge } from '@/nms/plugins/bridge.js'

// Интерфейсы и типы
interface PluginOptions {
  store?: any
}

interface IdObject {
  $id: string
  [key: string]: any
}

interface MetaField {
  name: string
  type: string
  place?: string
  group?: string
  polling?: string
  required?: boolean
  default?: any
  show?: string
  hide_pass?: string
  descriptor?: Record<string, any>
  cls?: string
  unit?: string
  id?: string
  data_type?: string
  hidden?: string
  alarmRule?: Array<{ expression: string; message: string }>
  warnRule?: Array<{ expression: string; message: string }>
  okRule?: Array<{ expression: string; message: string }>
  limit?: boolean
}

interface MetaRef {
  name: string
  cls: string
  place?: string
  group?: string
  polling?: string
  required?: boolean
  show?: string
}

interface MetaTemplateField {
  name: string
  place?: string
  group?: string
  polling?: string
  required?: boolean
  show?: string
}

interface MetaBridges {
  conference?: {
    metrics?: Array<{
      name: string
      id: string
      data_type: string
      hidden?: string
      unit?: string
      alarmRule?: Array<{ expression: string; message: string }>
      warnRule?: Array<{ expression: string; message: string }>
      okRule?: Array<{ expression: string; message: string }>
    }>
  }
  [key: string]: any
}

interface MetaData {
  bridges?: MetaBridges
  allow_bridges?: Record<string, any>
  manage?: Record<string, any>
  fields?: MetaField[]
  refs?: MetaRef[]
  template_fields?: MetaTemplateField[]
}

interface ModelField {
  name: string
  type: string
  value: any
  place?: string
  group?: string
  polling?: string
  ignore?: boolean
  required: boolean
  default: any
  readonly: boolean
  show: string
  cls?: string
  hide_pass?: boolean
  unit?: string
  id?: string
  source?: string
  data_type?: string
  hidden?: boolean
  alarmRule?: { expression: string; message: string }
  warnRule?: { expression: string; message: string }
  okRule?: { expression: string; message: string }
  limit?: boolean
  fields?: Record<string, any>
}

interface ModelData {
  fields: Record<string, ModelField>
  obj: Record<string, any>
  items: Record<string, any[]>
  links: any[]
  refs: Record<string, any>
  pattern: any
  bridges?: Record<string, any>
  manage?: Record<string, any>
}

type CompareOrder = number | string | Record<string, number>

const plugin: PluginObject<PluginOptions> = {
  install(VueInstance: typeof Vue, options?: PluginOptions) {
    const store = options?.store

    // $testMethod
    VueInstance.prototype.$testMethod = function(params: any): any {
      console.log('Test method called', params)
      return params
    }

    // $getTranslate
    VueInstance.prototype.$getTranslate = function(this: Vue, string: string): string {
      try {
        // Сохраняем оригинальную логику
        const translation = (this as any)._i18n?.messages?.[(this as any)._i18n?.locale]?.[string]
        return translation || string
      } catch (error) {
        return string
      }
    }

    // $createCrumbs
    VueInstance.prototype.$createCrumbs = async function(
      this: Vue,
      child: IdObject | null,
      caller?: any
    ): Promise<any[]> {
      if (!child || !child.$id) return []
      
      try {
        const obj$ = await $_bridge.getObjectById(child.$id)
        if (obj$) {
          const parents = await obj$.getParents()
          if (parents) {
            parents.splice(0, 1)
            return (this as any).$merge([], parents)
          } else {
            console.log('$createCrumbs', 'getParents', obj$, 'SMTH WRONG', parents)
            return []
          }
        } else {
          console.log('$createCrumbs', 'obj$', 'SMTH WRONG', obj$)
          return []
        }
      } catch (err) {
        console.error('CATCH', '$createCrumbs', err)
        return []
      }
    }

    // $loadMeta
    VueInstance.prototype.$loadMeta = async function(
      this: Vue,
      val: string | IdObject | IdObject[] | null,
      template?: string,
      caller?: any
    ): Promise<Record<string, any>> {
      if (!val) return {}
      console.log('$loadMeta', 'val', val, 'template', template, 'caller', caller)
      
      try {
        if (typeof val === 'string') {
          if (template) {
            const meta = await $_bridge.getMeta(val, template)
            return meta || {}
          } else {
            const meta = await $_bridge.getMeta(val)
            return meta || {}
          }
        } else if (val && typeof val === 'object' && '$id' in val) {
          const obj$ = await $_bridge.getObjectById(val.$id)
          const meta = await obj$.meta()
          return meta || {}
        } else if (Array.isArray(val) && val.length && val[0]?.$id) {
          const obj$ = await $_bridge.getObjectById(val[0].$id)
          const meta = await obj$.meta()
          return meta || {}
        } else {
          console.log('$loadMeta', 'caller', caller, 'val', val, 'SMTH WRONG')
          return {}
        }
      } catch (err) {
        console.error('CATCH', '$loadMeta', 'caller', caller, 'val', val, 'err', err)
        return {}
      }
    }

    // $prepareMeta
    VueInstance.prototype.$prepareMeta = async function(
      this: Vue,
      meta: MetaData,
      caller?: any
    ): Promise<ModelData | undefined> {
      try {
        const model: ModelData = {
          fields: {},
          obj: {},
          items: {},
          links: [],
          refs: {},
          pattern: null
        }

        const getEnum = async (cls: string): Promise<any[] | undefined> => {
          try {
            const items = await $_bridge.getEnum(cls)
            return items
          } catch (error) {
            console.error('CATCH', '$prepareMeta', 'getEnum', cls, 'SMTH WRONG', error)
            return undefined
          }
        }

        const loadRefs = async (cls: string): Promise<any[]> => {
          try {
            if (cls === 'measure') return []
            const children$ = await $_bridge.getObjectsByClass(cls)
            if (children$) {
              return children$
            } else {
              console.log('$prepareMeta', 'loadRefs', cls, 'SMTH WRONG', 'children$', children$)
              return []
            }
          } catch (error) {
            console.error('CATCH', '$prepareMeta', 'loadRefs', error)
            return []
          }
        }

        if (meta.bridges) {
          model.bridges = {}
          Object.keys(meta.bridges).forEach(key => {
            if (key !== 'calc') {
              Vue.set(model.bridges!, key, meta.bridges![key])
            }
          })
        }

        if (meta.allow_bridges) {
          if (!model.bridges) {
            model.bridges = {}
          }
          Object.keys(meta.allow_bridges).forEach(key => {
            if (key !== 'calc') {
              Vue.set(model.bridges!, key, meta.allow_bridges![key])
            }
          })
        }

        if (meta.manage) {
          model.manage = {}
          Object.keys(meta.manage).forEach(key => {
            if (key !== 'calc') {
              Vue.set(model.manage!, key, meta.manage![key])
            }
          })
        }

        if (meta.fields) {
          meta.fields.forEach(field => {

            if(field.ignore && field.ignore != 'false') {
              return
            }

            const fieldModel: ModelField = {
              name: field.name,
              type: field.type,
              value: field.type === 'timestamp' ? '' : null,
              place: field.place,
              group: field.group,
              polling: field.polling,
              required: field.required || false,
              default: field.default || false,
              readonly: false,
              show: field.name.substring(0, 1) === '$' ? 'false' : (field.hasOwnProperty('show') ? field.show! : 'false')
            }

            if (field.name === 'template') {
              fieldModel.type = 'select'
              fieldModel.cls = 'template'
            }

            if (field.type.substring(0, 4) === 'enum') {
              fieldModel.type = 'select_enum'
              fieldModel.cls = field.name
              getEnum(field.type.slice(5)).then(items => {
                if (items) {
                  Vue.set(model.items, field.name, items)
                }
              })
            }

            if (field.hide_pass) {
              fieldModel.hide_pass = field.hide_pass !== 'false'
            }

            if (field.type === 'timestamp' || field.type === 'TIMESTAMP WITH TIME ZONE') {
              fieldModel.readonly = true
            }

            if (field.name === 'state' || field.name === 'last_update_user') {
              fieldModel.readonly = true
            }

            if (field.type === 'json' && field.descriptor) {
              fieldModel.fields = {}
              
              Object.keys(field.descriptor).forEach(key => {
                fieldModel.fields![key] = {}
                
                Object.keys(field.descriptor).forEach(descriptorKey => {
                  fieldModel.fields![key][descriptorKey] = field.descriptor![descriptorKey]
                })

                fieldModel.fields![key].pathName = `${field.name}.${key}`
                fieldModel.fields![key].name = key
                fieldModel.fields![key].type = field.descriptor![key].type
                fieldModel.fields![key].value = field.descriptor![key].type === 'timestamp' ? '' : null
                fieldModel.fields![key].place = field.descriptor![key].place
                fieldModel.fields![key].group = field.descriptor![key].group
                fieldModel.fields![key].polling = field.descriptor![key].polling
                fieldModel.fields![key].show = key.substring(0, 1) === '$' 
                  ? 'false' 
                  : (field.descriptor![key].hasOwnProperty('show') ? field.descriptor![key].show : 'false')
                fieldModel.fields![key].default = field.descriptor![key].default || false

                if (field.descriptor![key].type?.substr(0, 4) === 'enum') {
                  fieldModel.fields![key].type = 'select_enum'
                  fieldModel.fields![key].cls = key
                  getEnum(field.descriptor![key].type.slice(5)).then(items => {
                    if (items) {
                      Vue.set(model.items, key, items)
                    }
                  })
                }

                if (field.descriptor![key].hide_pass) {
                  fieldModel.fields![key].hide_pass = field.descriptor![key].hide_pass !== 'false'
                }

                if (field.descriptor![key].unit) {
                  fieldModel.fields![key].unit = field.descriptor![key].unit
                }
              })
            }

            Vue.set(model.fields, field.name, fieldModel)
          })
        }

        if (meta.refs) {
          meta.refs.forEach(field => {

            if(field.ignore && field.ignore != 'false') {
              return
            }

            const fieldModel: ModelField = {
              name: field.name,
              type: 'select',
              value: null,
              cls: field.cls,
              place: field.place,
              group: field.group,
              polling: field.polling,
              required: field.required || false,
              readonly: false,
              show: field.name.substring(0, 1) === '$' ? 'false' : (field.hasOwnProperty('show') ? field.show! : 'false'),
              default: false
            }

            Vue.set(model.fields, field.name, fieldModel)

            if (!model.items.hasOwnProperty(field.cls)) {
              if(Array.isArray(field.cls)) {
                
              }
              else {
                loadRefs(field.cls).then(items => {
                  Vue.set(model.items, field.cls, items)
                })
              }           
            }
          })
        }

        if (meta.template_fields) {
          meta.template_fields.forEach(field => {

            if(field.ignore && field.ignore != 'false') {
              return
            }

            const fieldModel: ModelField = {
              name: field.name,
              type: field.type,
              // type: 'template',
              value: null,
              place: field.place,
              group: field.group,
              polling: field.polling,
              required: field.required || false,
              readonly: false,
              show: field.name.substring(0, 1) === '$' ? 'false' : (field.hasOwnProperty('show') ? field.show! : 'false'),
              default: false
            }

            if (field.type.substring(0, 4) === 'enum') {
              fieldModel.type = 'select_enum'
              fieldModel.cls = field.name
              getEnum(field.type.slice(5)).then(items => {
                if (items) {
                  Vue.set(model.items, field.name, items)
                }
              })
            }

            Vue.set(model.fields, field.name, fieldModel)
          })
        }

        if (meta?.bridges?.conference?.metrics?.length) {
          meta.bridges.conference.metrics.forEach(field => {
            const fieldModel: ModelField = {
              name: field.name,
              type: 'select',
              value: null,
              id: field.id,
              source: field.id,
              data_type: field.data_type,
              hidden: field.hidden && field.hidden !== 'false',
              unit: field.unit || null,
              alarmRule: { expression: '', message: '' },
              warnRule: { expression: '', message: '' },
              okRule: { expression: '', message: '' },
              required: false,
              readonly: false,
              default: false,
              show: 'true'
            }

            if (field.alarmRule?.length) {
              fieldModel.alarmRule = {
                expression: field.alarmRule[0].expression || '',
                message: field.alarmRule[0].message || ''
              }
            }
            if (field.warnRule?.length) {
              fieldModel.warnRule = {
                expression: field.warnRule[0].expression || '',
                message: field.warnRule[0].message || ''
              }
            }
            if (field.okRule?.length) {
              fieldModel.okRule = {
                expression: field.okRule[0].expression || '',
                message: field.okRule[0].message || ''
              }
            }

            Vue.set(model.fields, field.name, fieldModel)
          })
        }

        console.log('$prepareMeta', 'caller', caller, 'model', model)
        return model
      } catch (error) {
        console.error('CATCH', '$prepareMeta', 'error', error)
        return undefined
      }
    }

    // $prepareData
    VueInstance.prototype.$prepareData = async function(
      this: Vue,
      model: ModelData | null,
      val: string | IdObject | IdObject[] | null,
      meta: MetaData,
      caller?: any
    ): Promise<ModelData | undefined> {
      try {
        if (!model || !val) return undefined

        let data: any
        if (typeof val === 'string') {
          data = await $_bridge.getObjectById(val)
        } else if (Array.isArray(val) && val.length) {
          if (val[0].$id) {
            data = val[0]
          } else {
            console.log('$prepareData', 'caller', caller, 'val', val, 'SMTH WRONG')
            return undefined
          }
        } else {
          data = val
        }

        if (!data) {
          console.log('$prepareData', 'caller', caller, 'val', val, 'SMTH WRONG')
          return undefined
        }

        const fields = Object.keys(model.fields)
        fields.forEach(name => {
          if (data[name] && model.fields[name].type !== 'json') {
            Vue.set(model.fields[name], 'value', data[name])
            Vue.set(model.obj, name, data[name])
            
            if (name === 'name' && data.hasOwnProperty('full_name')) {
              Vue.set(model.fields[name], 'value', data.full_name)
            }
            if (data[name] === 'false') {
              Vue.set(model.obj, name, false)
            }
            if (data[name] === 'true') {
              Vue.set(model.obj, name, true)
            }
          }

          if (model.fields[name].type === 'json' && model.fields[name].hasOwnProperty('fields')) {
            Vue.set(model.obj, name, {})
            const sfields = Object.keys(model.fields[name].fields!)
            sfields.forEach(sname => {
              if (data[name] && data[name][sname]) {
                Vue.set(model.fields[name].fields![sname], 'value', data[name][sname])
                Vue.set(model.obj[name], sname, data[name][sname])
              }
              if (model.fields[name].fields![sname].default && !model.fields[name].fields![sname].value) {
                if(model.fields[name].fields![sname].type != 'boolean') {
                  Vue.set(model.fields[name].fields![sname], 'value', model.fields[name].fields![sname].default)
                  Vue.set(model.obj[name], sname, model.fields[name].fields![sname].default)
                }               
              }
            })
          }

          if (model.fields[name].default && !model.fields[name].value) {
            if (model.bridges) {
              if (Object.keys(model.bridges).some(bridge => bridge === model.fields[name].group)) {
                if (data[model.fields[name].group!] && data[model.fields[name].group!][name]) {
                  Vue.set(model.obj, name, data[model.fields[name].group!][name])
                } else {
                  if(model.fields[name].type != 'boolean') {
                    Vue.set(model.obj, name, model.fields[name].default)
                  }
                }
              }
            }
            //отменил присвоение дефолтного значения для полей с type === 'boolean'
            // if (model.fields[name].type === 'boolean') {
            //   if (model.fields[name].default === false || model.fields[name].default === 'false') {
            //     Vue.set(model.obj, name, false)
            //   }
            //   if (model.fields[name].default === true || model.fields[name].default === 'true') {
            //     Vue.set(model.obj, name, true)
            //   }
            // }
          }

          if (name === 'snmp$version' && data[name]) {
            Vue.set(model.obj, name, String(data[name]))
          }
        })

        if (!data.name && data.id) {
          const name$ = data.cls === 'module' ? data.id : `${data.id}_$${Math.round(Math.random() * 10000000000)}`
          Vue.set(model.obj, 'name', name$)
        }

        if (data.coordinates) {
          if (model.fields.latitude) {
            Vue.set(model.fields.latitude, 'value', data.coordinates.latitude)
          }
          if (model.fields.longitude) {
            Vue.set(model.fields.longitude, 'value', data.coordinates.longitude)
          }
        }

        if (meta?.refs?.length && data.$id) {
          const obj$ = data._cmdb_ ? data : await $_bridge.getObjectById(data.$id)
          const refs$ = await obj$.getRefs()
          if (refs$ && refs$.length) {
            refs$.forEach((ref$: any) => {
              if (model.fields[ref$.ref]) {
                Vue.set(model.fields[ref$.ref], 'value', ref$.hasOwnProperty('full_name') ? ref$.full_name : ref$.name)
                Vue.set(model.obj, ref$.ref, ref$.$id)

                if (model.fields[ref$.ref].limit) {
                  if (!model.fields[ref$.ref].value || !Array.isArray(model.fields[ref$.ref].value)) {
                    Vue.set(model.fields[ref$.ref], 'value', [])
                    const refName = ref$.hasOwnProperty('full_name') ? ref$.full_name : ref$.name
                    model.fields[ref$.ref].value.push(refName)
                  }
                }
              }
            })
          }
        }

        console.log('$prepareData', 'prepareData', 'model', model)
        return model
      } catch (error) {
        console.error('CATCH', '$prepareData', 'error', error)
        return undefined
      }
    }

    // $compare
    VueInstance.prototype.$compare = function(field?: CompareOrder, order?: number): (a: any, b: any) => number {
      try {
        const compareStrings = (a: any, b: any, orderSign: number = 1): number => {
          if (typeof a === 'string' && typeof b === 'string') {
            return orderSign * a.localeCompare(b, undefined, { sensitivity: 'base' })
          }
          if (a == null) return orderSign
          if (b == null) return -orderSign
          if (a < b) return -orderSign
          if (a > b) return orderSign
          return 0
        }

        const len = arguments.length
        
        if (len === 0) {
          return (a, b) => compareStrings(a, b)
        }
        
        if (len === 1) {
          if (typeof field === 'number') {
            return (a, b) => compareStrings(a, b, field < 0 ? -1 : 1)
          }
          if (typeof field === 'string') {
            return (a, b) => compareStrings(a[field], b[field])
          }
        }
        
        if (len === 2 && typeof order === 'number') {
          return (a, b) => compareStrings(a[field as string], b[field as string], order < 0 ? -1 : 1)
        }

        let fields: string[], orders: number[]
        
        if (typeof field === 'object' && field !== null) {
          fields = Object.keys(field)
          orders = fields.map(key => (field as Record<string, number>)[key])
        } else {
          fields = new Array(len)
          orders = new Array(len)
          for (let i = 0; i < len; i++) {
            fields[i] = arguments[i]
            orders[i] = 1
          }
        }
        
        return (a, b) => {
          for (let i = 0; i < fields.length; i++) {
            const result = compareStrings(a[fields[i]], b[fields[i]], orders[i])
            if (result !== 0) return result
          }
          return 0
        }
      } catch (error) {
        console.error('CATCH', '$compare', 'error', error)
        return () => 0
      }
    }

    // $getColor
    VueInstance.prototype.$getColor = function(this: Vue, status: string, caller?: any): string {
      if (store?.state?.books?.statuses?.length) {
        const color = store.state.books.statuses.find((item: any) => item.name === status)
        
        if (status === 'planned') {
          return '#1e4972'
        }
        if (status === 'in construction') {
          return 'red'
        }
        if (!color) {
          const unknownColor = store.state.books.statuses.find((item: any) => item.name === 'unknown')
          return unknownColor ? unknownColor.back_color : 'grey'
        }
        return color ? color.back_color : 'grey'
      }
      return 'grey'
    }

    // $checkItem
    VueInstance.prototype.$checkItem = function(this: Vue, item: IdObject, caller?: any): string {
      switch (item.cls) {
        case 'location':
          return item.type || 'undefined'
        case 'cabinet':
          return item.cls
        case 'device':
          return item.device_type || 'undefined'
        case 'module':
          return item.cls
        case 'port':
          return item.cls
        case 'socket':
          return item.cls
        case 'slot':
          return item.cls
        default:
          return 'undefined'
      }
    }
  }
}

export default plugin

// Расширение типов для Vue
declare module 'vue/types/vue' {
  interface Vue {
    $testMethod: (params: any) => any
    $getTranslate: (string: string) => string
    $createCrumbs: (child: IdObject | null, caller?: any) => Promise<any[]>
    $loadMeta: (val: string | IdObject | IdObject[] | null, template?: string, caller?: any) => Promise<Record<string, any>>
    $prepareMeta: (meta: MetaData, caller?: any) => Promise<ModelData | undefined>
    $prepareData: (model: ModelData | null, val: string | IdObject | IdObject[] | null, meta: MetaData, caller?: any) => Promise<ModelData | undefined>
    $compare: (field?: CompareOrder, order?: number) => (a: any, b: any) => number
    $getColor: (status: string, caller?: any) => string
    $checkItem: (item: IdObject, caller?: any) => string
  }
}